import { Button } from "@/components/ui/button";
import { Heart, Target, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useTrailPreference, useToggleFavorite, useToggleWantToGo } from "@/hooks/useTrailPreferences";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface TrailPreferenceButtonsProps {
  trailId: string;
  variant?: "default" | "compact";
  className?: string;
}

export const TrailPreferenceButtons = ({ 
  trailId, 
  variant = "default",
  className 
}: TrailPreferenceButtonsProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isFavorite, wantToGo, isLoading } = useTrailPreference(trailId);
  const toggleFavorite = useToggleFavorite();
  const toggleWantToGo = useToggleWantToGo();

  const handleFavoriteClick = () => {
    if (!user) {
      toast.info("Sign in to save favorites", {
        action: {
          label: "Sign In",
          onClick: () => navigate("/auth"),
        },
      });
      return;
    }
    toggleFavorite.mutate({ trailId, currentValue: isFavorite });
  };

  const handleWantToGoClick = () => {
    if (!user) {
      toast.info("Sign in to save to wishlist", {
        action: {
          label: "Sign In",
          onClick: () => navigate("/auth"),
        },
      });
      return;
    }
    toggleWantToGo.mutate({ trailId, currentValue: wantToGo });
  };

  const isUpdating = toggleFavorite.isPending || toggleWantToGo.isPending;

  if (variant === "compact") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleFavoriteClick}
          disabled={isLoading || isUpdating}
          className={cn(
            "h-8 px-2",
            isFavorite && "text-red-500 hover:text-red-600"
          )}
        >
          {toggleFavorite.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Heart className={cn("h-4 w-4", isFavorite && "fill-current")} />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleWantToGoClick}
          disabled={isLoading || isUpdating}
          className={cn(
            "h-8 px-2",
            wantToGo && "text-primary"
          )}
        >
          {toggleWantToGo.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Target className={cn("h-4 w-4", wantToGo && "fill-current")} />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={handleFavoriteClick}
        disabled={isLoading || isUpdating}
        className={cn(
          "w-full",
          isFavorite && "border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
        )}
      >
        {toggleFavorite.isPending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Heart className={cn("h-4 w-4 mr-2", isFavorite && "fill-current")} />
        )}
        {isFavorite ? "Favorited" : "Favorite"}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleWantToGoClick}
        disabled={isLoading || isUpdating}
        className={cn(
          "w-full",
          wantToGo && "border-primary text-primary hover:bg-primary/10"
        )}
      >
        {toggleWantToGo.isPending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Target className={cn("h-4 w-4 mr-2", wantToGo && "fill-current")} />
        )}
        {wantToGo ? "Want to Go" : "Want to Go"}
      </Button>
    </div>
  );
};
